import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  connect() {
    this.lastSubmittedUrl = null;
    this.isInitialized = false;
    this.submitTimeout = null;
    this.initializeFormState();
    this.syncAllCheckboxes();
    this.setupSearchInput();

    document.addEventListener('turbo:frame-load', (event) => {
      if (event.target.id === "research_items_data") {
        setTimeout(() => {
          this.initializeFormState();
          this.syncAllCheckboxes();
          this.setupSearchInput();
        }, 50);
      }
    });

    // Handle browser back/forward navigation
    window.addEventListener('popstate', () => {
      setTimeout(() => {
        this.initializeFormState();
        this.syncAllCheckboxes();
        this.setupSearchInput();
        this.submitFormIfNeeded();
      }, 100);
    });

    // Handle turbo navigation
    document.addEventListener('turbo:load', () => {
      this.initializeFormState();
      this.syncAllCheckboxes();
      this.setupSearchInput();
      this.submitFormIfNeeded();
    });

    this.element.addEventListener('initialize-form-state', (event) => {
      // Only initialize if not already done to prevent conflicts
      if (!this.isInitialized) {
        this.initializeFormState();
        this.syncAllCheckboxes();
        this.setupSearchInput();
        this.submitFormIfNeeded();
        this.isInitialized = true;
      }
    });
  }

  initializeFormState() {
    const form = document.getElementById('research-item-filter-form');
    if (!form) return;

    // Get current URL parameters
    const urlParams = new URLSearchParams(window.location.search);

    // Initialize search input
    const searchInput = document.getElementById('title-search-input');
    if (searchInput) {
      let searchValue = urlParams.get('q[title_cont]') || '';

      // If URL doesn't have search value but sessionStorage does, restore it
      if (!searchValue) {
        const storedValue = sessionStorage.getItem('research_search_value');
        if (storedValue) {
          searchValue = storedValue;
        }
      }

      searchInput.value = searchValue;
      this.updateSearchIcon(searchValue);
    }

    // Initialize content type checkboxes
    const contentTypeValues = urlParams.getAll('q[content_type_in][]');
    this.initializeCheckboxes('input[name="q[content_type_in][]"]', contentTypeValues);

    // Initialize sector checkboxes
    const sectorValues = urlParams.getAll('q[gics_industry_id_in][]');
    this.initializeCheckboxes('input[name="q[gics_industry_id_in][]"]', sectorValues);
  }

  initializeCheckboxes(selector, selectedValues) {
    const checkboxes = document.querySelectorAll(`#research-item-filter-form ${selector}`);
    checkboxes.forEach(checkbox => {
      const value = checkbox.value;
      if (selectedValues.includes(value)) {
        checkbox.checked = true;
      } else {
        checkbox.checked = false;
      }
    });
  }

  setupSearchInput() {
    const searchInput = document.getElementById('title-search-input');
    if (!searchInput) return;

    // Remove existing listeners to prevent duplicates
    searchInput.removeEventListener('input', this.handleSearchInput.bind(this));
    searchInput.removeEventListener('keypress', this.handleSearchKeypress.bind(this));

    // Add new listeners
    searchInput.addEventListener('input', this.handleSearchInput.bind(this));
    searchInput.addEventListener('keypress', this.handleSearchKeypress.bind(this));
  }

  handleSearchInput(event) {
    this.updateSearchIcon(event.target.value);
  }

  handleSearchKeypress(event) {
    if (event.key === 'Enter') {
      // Clear any existing timeout to prevent race conditions
      if (this.submitTimeout) {
        clearTimeout(this.submitTimeout);
      }

      submitTitleSearch();
    }
  }

  updateSearchIcon(value, isSubmitted = false) {
    const searchBtn = document.getElementById('search-btn');
    const clearBtn = document.getElementById('clear-search-btn');

    if (!searchBtn || !clearBtn) return;

    if (value.trim() === '') {
      searchBtn.classList.remove('hidden');
      clearBtn.classList.add('hidden');
    } else {
      searchBtn.classList.remove('hidden');
      clearBtn.classList.remove('hidden');
    }
  }

  submitFormIfNeeded() {
    const currentUrl = window.location.search;

    // Prevent infinite loops by checking if we've already submitted for this URL
    if (this.lastSubmittedUrl === currentUrl) {
      return;
    }

    const urlParams = new URLSearchParams(currentUrl);
    const hasFilters = urlParams.has('q[title_cont]') ||
                      urlParams.has('q[content_type_in][]') ||
                      urlParams.has('q[gics_industry_id_in][]');

    if (hasFilters) {
      // Use a longer delay to ensure everything is properly initialized
      setTimeout(() => {
        this.lastSubmittedUrl = currentUrl;
        this.submitForm();
      }, 300);
    }
  }

  syncAllCheckboxes() {
    this.setupGroup(
      '#research-item-filter-form input.content-type-filter',
      '#research-item-filter-form #content-type-all'
    );

    this.setupSectorGroup();
  }

  setupGroup(groupSelector, allSelector) {
    const group = document.querySelectorAll(groupSelector);
    const all = document.querySelector(allSelector);
    if (!all || group.length === 0) return;

    const updateAllCheckbox = () => {
      const checkedCount = Array.from(group).filter(cb => cb.checked).length;
      if (checkedCount === 0) {
        all.checked = true;
        all.disabled = false;
      } else if (checkedCount === group.length) {
        group.forEach(cb => cb.checked = false);
        all.checked = true;
        all.disabled = false;
      } else {
        all.checked = false;
        all.disabled = false;
      }
    };

    const handleIndividualCheckboxChange = () => {
      updateAllCheckbox();
      this.debouncedSubmitForm();
    };

    group.forEach(cb => {
      // Remove existing listeners to prevent duplicates
      cb.removeEventListener('change', handleIndividualCheckboxChange);
      cb.addEventListener('change', handleIndividualCheckboxChange);
    });

    all.addEventListener('change', (e) => {
      if (all.checked) {
        group.forEach(cb => cb.checked = false);
        all.disabled = false;
        this.debouncedSubmitForm();
      }
      updateAllCheckbox();
    });

    updateAllCheckbox();
  }

  setupSectorGroup() {
    const sectorCheckboxes = document.querySelectorAll('#research-item-filter-form input.sector-filter');
    const sectorAll = document.querySelector('#research-item-filter-form #sector-all');

    if (!sectorAll || sectorCheckboxes.length === 0) return;

    const updateSectorAllCheckbox = () => {
      const checkedCount = Array.from(sectorCheckboxes).filter(cb => cb.checked).length;
      if (checkedCount === 0) {
        sectorAll.checked = true;
        sectorAll.disabled = false;
      } else if (checkedCount === sectorCheckboxes.length) {
        sectorCheckboxes.forEach(cb => cb.checked = false);
        sectorAll.checked = true;
        sectorAll.disabled = false;
      } else {
        sectorAll.checked = false;
        sectorAll.disabled = false;
      }
    };

    const handleSectorCheckboxChange = () => {
      updateSectorAllCheckbox();
      this.debouncedSubmitForm();
    };

    sectorCheckboxes.forEach(cb => {
      // Remove existing listeners to prevent duplicates
      cb.removeEventListener('change', handleSectorCheckboxChange);
      cb.addEventListener('change', handleSectorCheckboxChange);
    });

    sectorAll.addEventListener('change', (e) => {
      if (sectorAll.checked) {
        sectorCheckboxes.forEach(cb => cb.checked = false);
        sectorAll.disabled = false;
        this.debouncedSubmitForm();
      }
      updateSectorAllCheckbox();
    });

    updateSectorAllCheckbox();
  }



  debouncedSubmitForm() {
    // Clear any existing timeout
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
    }

    // Set a new timeout to submit the form after 300ms of inactivity
    this.submitTimeout = setTimeout(() => {
      this.submitForm();
    }, 300);
  }

  submitForm() {
    const form = document.getElementById('research-item-filter-form');
    if (form) {
      this.lastSubmittedUrl = null;
      this.updateUrlOnSubmit(form);
      form.requestSubmit();
    }
  }

  updateUrlOnSubmit(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    // Filter out empty values before adding to URL params
    for (const [key, value] of formData.entries()) {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    }

    const queryString = params.toString();
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }

  handleCustomFilterReset() {
    const form = document.getElementById('research-item-filter-form');
    if (!form) return;

    // Reset all form inputs
    form.querySelectorAll('input').forEach(input => {
      const type = input.type;

      if (type === 'checkbox' || type === 'radio') {
        input.checked = false;
      } else if (type === 'text') {
        input.value = '';
      }
    });

    // Reset search input specifically
    const searchInput = document.getElementById('title-search-input');
    if (searchInput) {
      searchInput.value = '';
    }

    // Clear stored search value
    sessionStorage.removeItem('research_search_value');

    // Reset search icon state
    const searchBtn = document.getElementById('search-btn');
    const clearBtn = document.getElementById('clear-search-btn');
    if (searchBtn && clearBtn) {
      searchBtn.classList.remove('hidden');
      clearBtn.classList.add('hidden');
    }

    // Re-sync checkboxes after reset
    this.syncAllCheckboxes();

    // Submit the form to apply the reset
    this.submitForm();
  }
}
